import {
  ArrowBack,
  CheckCircle,
  ChevronRight,
  Close,
  ExpandLess,
  ExpandMore,
  FilterList,
  HourglassEmpty,
  Info,
  Search,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useFetch from "src/Components/Common/useFetch";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import {
  trackHoldsCategoryOpened,
  trackHoldsDetailSearched,
  trackHoldsResolutionViewed,
} from "src/Utils/Mixpanel/VendorPortalEvents";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { useVendorPortal } from "./VendorPortalLayout";

// ─── API types ────────────────────────────────────────────────────────────────

// Fixed tabs shown always (panLevelHold excluded)
const ALL_TABS: { tab: string; label: string }[] = [
  { tab: "gst", label: "GST Related Hold" },
  { tab: "prRtv", label: "PR/RTV Related Hold" },
  { tab: "bdpo", label: "BDPO Related Hold" },
  { tab: "adsVisibility", label: "Ads Visibility Related Hold" },
];

interface SummaryItem {
  tab: string;
  label: string;
  sumAmount: string;
  count: number;
}

interface SummaryData {
  pending: SummaryItem[];
  resolved: SummaryItem[];
}

interface TabEntriesData {
  tab: string;
  status: string;
  columns: string[];
  rows: Record<string, string>[];
}

// ─── Active detail state ──────────────────────────────────────────────────────

interface ActiveDetail {
  tab: string;
  status: "pending" | "resolved";
  label: string;
  sumAmount: string;
  count: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtAmt = (raw: string | number) => {
  const n = Number(raw);
  return Number.isFinite(n) ? formatMoney(ToDineroObj(n)) : "—";
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const MetricCard = ({
  label,
  value,
  valueColor = "#1A1A2E",
}: {
  label: string;
  value: React.ReactNode;
  valueColor?: string;
}) => (
  <Paper elevation={0} sx={{ border: "1px solid #E5E3EF", borderRadius: "10px", p: "14px 16px", flex: 1, minWidth: 0 }}>
    <Typography sx={{ fontSize: 11, color: "#6B6B8A", fontWeight: 500, mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 17, fontWeight: 700, color: valueColor }}>{value}</Typography>
  </Paper>
);

const Disclaimer = ({ text }: { text: string }) => (
  <Box
    sx={{
      bgcolor: "#F0F4FF",
      border: "1px solid #C7D7FE",
      borderRadius: "8px",
      p: "12px 16px",
      display: "flex",
      alignItems: "flex-start",
      gap: 1.25,
      mt: 1.75,
    }}
  >
    <Info sx={{ width: 15, height: 15, color: "#2D1B69", mt: 0.125, flexShrink: 0 }} />
    <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{text}</Typography>
  </Box>
);

// ─── Hold Card ────────────────────────────────────────────────────────────────

const HoldCard = ({
  item,
  resolved,
  onClick,
  disabled = false,
}: {
  item: SummaryItem;
  resolved: boolean;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Paper
    elevation={0}
    onClick={disabled ? undefined : onClick}
    sx={{
      border: "1px solid",
      borderColor: resolved ? "#A7F3D0" : "#E5E3EF",
      borderRadius: "10px",
      p: "16px 18px",
      cursor: disabled ? "default" : "pointer",
      transition: "border-color .15s, box-shadow .15s",
      bgcolor: resolved ? "#FAFFF8" : "#fff",
      display: "flex",
      flexDirection: "column",
      gap: 1,
      "&:hover": disabled
        ? {}
        : {
            borderColor: resolved ? "#059669" : "#2D1B69",
            boxShadow: resolved ? "0 2px 12px rgba(5,150,105,0.08)" : "0 2px 12px rgba(45,27,105,0.08)",
          },
    }}
  >
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "8px",
        bgcolor: resolved ? "#D1FAE5" : "#EDE9FE",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {resolved ? (
        <CheckCircle sx={{ width: 16, height: 16, color: "#059669" }} />
      ) : (
        <HourglassEmpty sx={{ width: 16, height: 16, color: "#7C3AED" }} />
      )}
    </Box>
    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#2D1B69" }}>{item.label}</Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pt: 1,
        borderTop: "1px solid #F0EEF8",
      }}
    >
      <Typography
        sx={{ fontSize: 16, fontWeight: 700, color: disabled ? "#9CA3AF" : resolved ? "#059669" : "#DC2626" }}
      >
        {disabled ? "—" : fmtAmt(item.sumAmount)}
        {!disabled && (
          <Typography component="span" sx={{ fontSize: 13, color: "#6B6B8A", fontWeight: 400 }}>
            {" "}
            ({item.count})
          </Typography>
        )}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 500, color: disabled ? "#9CA3AF" : resolved ? "#059669" : "#2D1B69" }}
        >
          {disabled ? "No data" : "View Details"}
        </Typography>
        {!disabled && <ChevronRight sx={{ fontSize: 14, color: resolved ? "#059669" : "#2D1B69" }} />}
      </Box>
    </Box>
  </Paper>
);

// ─── Collapsible bucket ───────────────────────────────────────────────────────

const Bucket = ({
  title,
  totalAmount,
  totalCount,
  resolved = false,
  defaultOpen = false,
  children,
}: {
  title: string;
  totalAmount: number;
  totalCount: number;
  resolved?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper elevation={0} sx={{ border: "1px solid #E5E3EF", borderRadius: "12px", mb: 2, overflow: "hidden" }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          p: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { bgcolor: "#FAFAFA" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#1A1A2E" }}>{title}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500, color: resolved ? "#059669" : "#DC2626" }}>
            {fmtAmt(totalAmount)}
          </Typography>
          {totalCount > 0 && <Typography sx={{ fontSize: 13, color: "#6B6B8A" }}>({totalCount} entries)</Typography>}
        </Box>
        {open ? (
          <ExpandLess sx={{ width: 20, height: 20, color: "#6B6B8A" }} />
        ) : (
          <ExpandMore sx={{ width: 20, height: 20, color: "#6B6B8A" }} />
        )}
      </Box>
      <Collapse in={open}>
        <Box
          sx={{
            borderTop: "1px solid #F0EEF8",
            p: "16px 20px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Detail header ────────────────────────────────────────────────────────────

const DetailHeader = ({ title, onBack }: { title: string; onBack: () => void }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <IconButton
      onClick={onBack}
      size="small"
      sx={{ border: "1px solid #E5E3EF", borderRadius: "7px", bgcolor: "#fff", width: 30, height: 30 }}
    >
      <ArrowBack sx={{ fontSize: 14, color: "#374151" }} />
    </IconButton>
    <Typography sx={{ fontSize: 13, color: "#6B6B8A" }}>Hold Resolution</Typography>
    <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>›</Typography>
    <Typography sx={{ fontSize: 17, fontWeight: 600, color: "#1A1A2E" }}>{title}</Typography>
  </Box>
);

// ─── Pagination row ───────────────────────────────────────────────────────────

const PaginationRow = ({
  page,
  totalPages,
  from,
  to,
  total,
  onPage,
}: {
  page: number;
  totalPages: number;
  from: number;
  to: number;
  total: number;
  onPage: (p: number) => void;
}) => {
  const pages: (number | "...")[] = [];
  if (totalPages <= 7) {
    for (let i = 0; i < totalPages; i++) pages.push(i);
  } else {
    pages.push(0);
    if (page > 2) pages.push("...");
    for (let i = Math.max(1, page - 1); i <= Math.min(totalPages - 2, page + 1); i++) pages.push(i);
    if (page < totalPages - 3) pages.push("...");
    pages.push(totalPages - 1);
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: "10px 16px",
        borderTop: "1px solid #F0EEF8",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#6B6B8A" }}>
        Showing {from}–{to} of {total}
      </Typography>
      <Box sx={{ display: "flex", gap: "3px" }}>
        {pages.map((p, i) => (
          <Box
            key={i}
            onClick={() => typeof p === "number" && onPage(p)}
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              border: "1px solid",
              borderColor: p === page ? "#2D1B69" : "#E5E3EF",
              bgcolor: p === page ? "#2D1B69" : "transparent",
              color: p === page ? "#fff" : "#374151",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 11,
              cursor: typeof p === "number" ? "pointer" : "default",
            }}
          >
            {p === "..." ? "…" : (p as number) + 1}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Filter Modal ─────────────────────────────────────────────────────────────

const DROPDOWN_COL_NAMES = new Set([
  "Status",
  "Resolved Status",
  "Hold Bucket",
  "Hold Buckets",
  "Type of Hold",
  "Source Type",
  "Document Type",
  "FY",
  "Entity",
  "WH",
  "Brand",
]);

type DetectedColType = "dropdown" | "date" | "amount" | "text";

const detectColType = (col: string): DetectedColType => {
  if (DROPDOWN_COL_NAMES.has(col)) return "dropdown";
  if (/date/i.test(col)) return "date";
  if (/amount|value|tax/i.test(col)) return "amount";
  return "text";
};

const inputSx = {
  width: "100%",
  height: 34,
  border: "1px solid #E5E3EF",
  borderRadius: "6px",
  px: "10px",
  fontSize: 12,
  fontFamily: "inherit",
  color: "#374151",
  outline: "none",
  backgroundColor: "#fff",
  boxSizing: "border-box",
  "&:focus": { borderColor: "#2D1B69" },
} as const;

const FieldLabel = ({ text }: { text: string }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#6B6B8A", mb: 0.5 }}>{text}</Typography>
);

const FilterModal = ({
  open,
  onClose,
  columns,
  rows,
  filters,
  onChange,
  onClear,
}: {
  open: boolean;
  onClose: () => void;
  columns: string[];
  rows: Record<string, string>[];
  filters: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
}) => {
  const uniqueOpts: Record<string, string[]> = {};
  for (const col of columns) {
    if (detectColType(col) === "dropdown") {
      const vals = new Set<string>();
      for (const row of rows) {
        if (row[col] !== undefined && row[col] !== null && String(row[col]).trim() !== "") {
          vals.add(String(row[col]));
        }
      }
      uniqueOpts[col] = Array.from(vals).sort();
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid #E5E3EF",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterList sx={{ fontSize: 16, color: "#2D1B69" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1A1A2E" }}>Filters</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Button
            size="small"
            onClick={onClear}
            sx={{ fontSize: 11, color: "#6B6B8A", textTransform: "none", px: 1, minWidth: 0 }}
          >
            Clear All
          </Button>
          <Box
            onClick={onClose}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#6B6B8A",
              p: 0.5,
              borderRadius: "4px",
              "&:hover": { backgroundColor: "#F0EEF8" },
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </Box>
        </Box>
      </Box>

      {/* Filter fields — 3-column grid */}
      <Box
        sx={{
          overflowY: "auto",
          maxHeight: "65vh",
          px: 2.5,
          py: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 2,
        }}
      >
        {columns.map((col) => {
          const type = detectColType(col);
          const label = <FieldLabel text={col} />;

          if (type === "dropdown") {
            const selected = (filters[col] as string[]) || [];
            const opts = uniqueOpts[col] || [];
            if (opts.length === 0) return null;
            return (
              <Box key={col}>
                {label}
                <Select
                  multiple
                  displayEmpty
                  size="small"
                  value={selected}
                  onChange={(e) => onChange(col, e.target.value as string[])}
                  input={
                    <OutlinedInput
                      notched={false}
                      sx={{
                        width: "100%",
                        fontSize: 12,
                        height: 34,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E3EF" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E3EF" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2D1B69",
                          borderWidth: "1px",
                        },
                      }}
                    />
                  }
                  renderValue={(s) =>
                    (s as string[]).length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>Select…</Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
                        {(s as string[]).map((v) => (
                          <Chip key={v} label={v} size="small" sx={{ fontSize: 10, height: 18 }} />
                        ))}
                      </Box>
                    )
                  }
                  MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                  sx={{ width: "100%" }}
                >
                  {opts.map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontSize: 12, py: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={selected.includes(o)}
                        sx={{ p: 0, mr: 1, "& .MuiSvgIcon-root": { fontSize: 14 } }}
                      />
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            );
          } else if (type === "date") {
            const range = (filters[col] as { from: string; to: string }) || { from: "", to: "" };
            return (
              <Box key={col}>
                {label}
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="date"
                      value={range.from}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(col, { ...range, from: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.25 }}>From</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="date"
                      value={range.to}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(col, { ...range, to: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.25 }}>To</Typography>
                  </Box>
                </Box>
              </Box>
            );
          } else if (type === "amount") {
            const range = (filters[col] as { min: string; max: string }) || { min: "", max: "" };
            return (
              <Box key={col}>
                {label}
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="number"
                      placeholder="Min"
                      value={range.min}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(col, { ...range, min: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.25 }}>Min (₹)</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box
                      component="input"
                      type="number"
                      placeholder="Max"
                      value={range.max}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(col, { ...range, max: e.target.value })
                      }
                      sx={inputSx}
                    />
                    <Typography sx={{ fontSize: 10, color: "#9CA3AF", mt: 0.25 }}>Max (₹)</Typography>
                  </Box>
                </Box>
              </Box>
            );
          } else {
            const val = (filters[col] as string) || "";
            return (
              <Box key={col}>
                {label}
                <Box
                  component="input"
                  placeholder="Search…"
                  value={val}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(col, e.target.value)}
                  sx={inputSx}
                />
              </Box>
            );
          }
        })}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #E5E3EF",
          flexShrink: 0,
        }}
      >
        <Button
          size="small"
          onClick={onClear}
          sx={{
            fontSize: 12,
            color: "#6B6B8A",
            border: "1px solid #E5E3EF",
            borderRadius: "7px",
            px: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "#FAFAFA" },
          }}
        >
          Clear All
        </Button>
        <Button
          size="small"
          onClick={onClose}
          sx={{
            fontSize: 12,
            color: "#374151",
            border: "1px solid #E5E3EF",
            borderRadius: "7px",
            px: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "#FAFAFA" },
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          onClick={onClose}
          sx={{
            fontSize: 12,
            color: "#fff",
            backgroundColor: "#2D1B69",
            borderRadius: "7px",
            px: 2.5,
            textTransform: "none",
            "&:hover": { backgroundColor: "#1e1248" },
          }}
        >
          Apply
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Detail view ──────────────────────────────────────────────────────────────

const ROWS_PER_PAGE = 10;

const DetailView = ({
  detail,
  entityName,
  token,
  onBack,
}: {
  detail: ActiveDetail;
  entityName: string | null;
  token: string;
  onBack: () => void;
}) => {
  const [entries, setEntries] = useState<TabEntriesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [colFilters, setColFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    setLoading(true);
    setPage(0);
    const ep = API_ENDPOINTS.VP_ON_HOLD_TAB_ENTRIES(detail.tab, detail.status);
    useFetch(ep.url, "GET", {
      failureMessage: ep.failureMessage,
      config: {
        headers: { Authorization: `Bearer ${token}` },
        params: entityName ? { entityName } : {},
      },
      thenCallBack: (res) => {
        setEntries(res.data?.data ?? null);
        setLoading(false);
      },
      catchCallBack: () => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.tab, detail.status, entityName]);

  const titleSuffix = detail.status === "resolved" ? " — Resolved" : "";
  const title = `${detail.label}${titleSuffix}`;

  const visibleColumns = (entries?.columns ?? []).filter((col) => col !== "Hold Bucket" && col !== "Hold Buckets");

  const activeFilterCount = Object.values(colFilters).filter((v) => {
    if (Array.isArray(v)) return v.length > 0;
    if (v && typeof v === "object") return Object.values(v).some((x) => x !== "" && x !== null);
    return v !== "" && v !== null && v !== undefined;
  }).length;

  const filteredRows = (entries?.rows ?? []).filter((row) => {
    if (search) {
      const q = search.toLowerCase();
      if (!Object.values(row).some((v) => String(v).toLowerCase().includes(q))) return false;
    }

    for (const [col, filterVal] of Object.entries(colFilters)) {
      const type = detectColType(col);
      const cellVal = row[col];

      if (type === "dropdown") {
        const selected = filterVal as string[];
        if (selected && selected.length > 0 && !selected.includes(String(cellVal ?? ""))) return false;
      } else if (type === "text") {
        const q = ((filterVal as string) || "").toLowerCase();
        if (
          q &&
          !String(cellVal ?? "")
            .toLowerCase()
            .includes(q)
        )
          return false;
      } else if (type === "date") {
        const range = filterVal as { from: string; to: string };
        if (range && (range.from || range.to)) {
          if (!cellVal) return false;
          const d = new Date(cellVal);
          if (range.from && d < new Date(range.from)) return false;
          if (range.to && d > new Date(range.to + "T23:59:59")) return false;
        }
      } else if (type === "amount") {
        const range = filterVal as { min: string; max: string };
        if (range) {
          const amount = Number(cellVal);
          if (range.min !== "" && range.min !== undefined && amount < Number(range.min)) return false;
          if (range.max !== "" && range.max !== undefined && amount > Number(range.max)) return false;
        }
      }
    }

    return true;
  });
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ROWS_PER_PAGE));
  const paginated = filteredRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);
  const from = filteredRows.length === 0 ? 0 : page * ROWS_PER_PAGE + 1;
  const to = Math.min((page + 1) * ROWS_PER_PAGE, filteredRows.length);

  return (
    <>
      <DetailHeader title={title} onBack={onBack} />

      {/* Metric cards */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2.25 }}>
        <MetricCard
          label="Total Hold Amount"
          value={fmtAmt(detail.sumAmount)}
          valueColor={detail.status === "resolved" ? "#059669" : "#DC2626"}
        />
        <MetricCard label="Entries" value={detail.count.toLocaleString("en-IN")} />
      </Box>

      {/* Table card */}
      <Paper elevation={0} sx={{ border: "1px solid #E5E3EF", borderRadius: "12px", overflow: "hidden" }}>
        {/* Toolbar */}
        <Box
          sx={{
            p: "13px 18px",
            borderBottom: "1px solid #F0EEF8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1A1A2E" }}>{title}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={(e) => {
                trackHoldsDetailSearched({ search_term: e.target.value });
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 14, color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 12, height: 32 },
              }}
              sx={{ width: 190, "& .MuiOutlinedInput-root": { borderRadius: "7px" } }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterList sx={{ fontSize: 14 }} />}
              onClick={() => setFilterOpen(true)}
              sx={{
                fontSize: 12,
                color: activeFilterCount > 0 ? "#2D1B69" : "#374151",
                borderColor: activeFilterCount > 0 ? "#2D1B69" : "#E5E3EF",
                backgroundColor: activeFilterCount > 0 ? "#F0EEF8" : "transparent",
                borderRadius: "7px",
                textTransform: "none",
                height: 32,
                "&:hover": { backgroundColor: activeFilterCount > 0 ? "#E5E0F5" : undefined },
              }}
            >
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </Button>
          </Box>
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={26} sx={{ color: "#2D1B69" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6B6B8A",
                        bgcolor: "#FAFAFA",
                        borderBottom: "1px solid #F0EEF8",
                        whiteSpace: "nowrap",
                        py: "9px",
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length || 1}
                      sx={{ textAlign: "center", py: 5, fontSize: 12, color: "#9CA3AF", borderBottom: "none" }}
                    >
                      No entries found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((row, ri) => (
                    <TableRow
                      key={ri}
                      hover
                      sx={{ "&:last-child td": { borderBottom: 0 }, "&:hover td": { bgcolor: "#FAFAFA" } }}
                    >
                      {visibleColumns.map((col) => (
                        <TableCell
                          key={col}
                          sx={{
                            fontSize: 12,
                            color: "#374151",
                            borderBottom: "1px solid #F8F7FC",
                            py: "11px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row[col] ?? "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && filteredRows.length > 0 && (
          <PaginationRow
            page={page}
            totalPages={totalPages}
            from={from}
            to={to}
            total={filteredRows.length}
            onPage={setPage}
          />
        )}
      </Paper>

      <Disclaimer text="Hold data is based on information uploaded by Instamart Finance. For queries, please use Raise Other Issues." />

      <FilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        columns={visibleColumns}
        rows={entries?.rows ?? []}
        filters={colFilters}
        onChange={(col, value) => {
          setColFilters((prev) => ({ ...prev, [col]: value }));
          setPage(0);
        }}
        onClear={() => {
          setColFilters({});
          setPage(0);
        }}
      />
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

interface Company {
  companyCode: string;
  companyName: string;
}

const HoldResolutionVendorPortal = () => {
  const { handleUnauthorizedError, isSessionReady, token } = useVendorPortal();
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [activeDetail, setActiveDetail] = useState<ActiveDetail | null>(null);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Company | null>(null); // null = All

  const entityName = selectedOrg?.companyName ?? null;

  const getAllCompanies = (tkn: string) => {
    useFetch(API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_ALL_COMPANIES.url, "GET", {
      failureMessage: API_ENDPOINTS.FETCH_VP_LEDGER_SUMMARY_GET_ALL_COMPANIES.failureMessage,
      config: { headers: { Authorization: `Bearer ${tkn}` } },
      thenCallBack: (res) => setAllCompanies(res.data?.data ?? []),
      catchCallBack: (err) => handleUnauthorizedError(err),
    });
  };

  const fetchSummary = (entity: string | null, tkn: string) => {
    setSummaryLoading(true);
    useFetch(API_ENDPOINTS.VP_ON_HOLD_SUMMARY.url, "GET", {
      failureMessage: API_ENDPOINTS.VP_ON_HOLD_SUMMARY.failureMessage,
      config: {
        headers: { Authorization: `Bearer ${tkn}` },
        params: entity ? { entityName: entity } : {},
      },
      thenCallBack: (res) => {
        setSummary(res.data?.data ?? null);
        setSummaryLoading(false);
      },
      catchCallBack: () => setSummaryLoading(false),
    });
  };

  useEffect(() => {
    if (!isSessionReady || !token) return;
    trackHoldsResolutionViewed();
    getAllCompanies(token);
    fetchSummary(entityName, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionReady, token]);

  useEffect(() => {
    if (!token) return;
    fetchSummary(entityName, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  const pendingItems = summary?.pending ?? [];
  const resolvedItems = summary?.resolved ?? [];

  // Merge fixed tabs with API data — always show all tabs
  const buildTabItems = (apiItems: SummaryItem[]): SummaryItem[] =>
    ALL_TABS.map(({ tab, label }) => {
      const found = apiItems.find((i) => i.tab === tab);
      return found ?? { tab, label, sumAmount: "0", count: 0 };
    });

  const pendingTabItems = buildTabItems(pendingItems);
  const resolvedTabItems = buildTabItems(resolvedItems);

  const pendingTotal = pendingTabItems.reduce((s, i) => s + Number(i.sumAmount), 0);
  const pendingCount = pendingTabItems.reduce((s, i) => s + i.count, 0);
  const resolvedTotal = resolvedTabItems.reduce((s, i) => s + Number(i.sumAmount), 0);
  const resolvedCount = resolvedTabItems.reduce((s, i) => s + i.count, 0);

  const openDetail = (item: SummaryItem, status: "pending" | "resolved") => {
    setActiveDetail({ tab: item.tab, status, label: item.label, sumAmount: item.sumAmount, count: item.count });
    trackHoldsCategoryOpened({
      hold_category: item.label,
      hold_amount: Number(item.sumAmount),
      entry_count: item.count,
      bucket: status,
    });
    window.scrollTo(0, 0);
  };

  return (
    <Box sx={{ p: "20px 28px" }}>
      {/* ── Detail view ── */}
      {activeDetail && (
        <DetailView detail={activeDetail} entityName={entityName} token={token} onBack={() => setActiveDetail(null)} />
      )}

      {/* ── Summary view ── */}
      {!activeDetail && (
        <>
          {/* Org selector */}
          <Box sx={{ mb: 2.5 }}>
            <Autocomplete
              options={allCompanies.sort((a, b) => a.companyName.localeCompare(b.companyName))}
              getOptionLabel={(o) => o.companyName}
              value={selectedOrg}
              onChange={(_e, val) => setSelectedOrg(val)}
              size="small"
              sx={{ width: 280 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Organisation Name" className="update-custom-border-radius" />
              )}
            />
          </Box>

          {summaryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} sx={{ color: "#2D1B69" }} />
            </Box>
          ) : (
            <>
              <Bucket title="Pending" totalAmount={pendingTotal} totalCount={pendingCount} defaultOpen={true}>
                {pendingTabItems.map((item) => (
                  <HoldCard
                    key={item.tab}
                    item={item}
                    resolved={false}
                    onClick={() => item.count > 0 && openDetail(item, "pending")}
                    disabled={item.count === 0}
                  />
                ))}
              </Bucket>

              <Bucket
                title="Resolved"
                totalAmount={resolvedTotal}
                totalCount={resolvedCount}
                resolved
                defaultOpen={false}
              >
                {resolvedTabItems.map((item) => (
                  <HoldCard
                    key={item.tab}
                    item={item}
                    resolved={true}
                    onClick={() => item.count > 0 && openDetail(item, "resolved")}
                    disabled={item.count === 0}
                  />
                ))}
              </Bucket>

              <Disclaimer text="Holds shown here are based on data uploaded by Instamart Finance. Resolved holds are those where the vendor action has been accepted and hold has been released. For queries, please use Raise Other Issues." />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default HoldResolutionVendorPortal;
